// @ts-check

/**
 * @file Configures the frontend Velocious runtime.
 */
/** @import {OfflineGrantSigningKey} from "../../../shared/offline-grant-signing-keys.js" */

import "../shared/promise-with-resolvers"

import BrowserEnvironmentHandler from "velocious/build/src/environment-handlers/browser.js"
import Configuration from "velocious/build/src/configuration.js"
import SingleMultiUsePool from "velocious/build/src/database/pool/single-multi-use.js"
import SqliteDriver from "velocious/build/src/database/drivers/sqlite/index"
import VelociousDatabaseInitializer from "velocious/build/src/database/initializer-from-require-context.js"

import {expoPublicEnv, expoPublicEnvFlag} from "../shared/expo-public-env"
import {PROJECT_REPLICA_DATABASE_IDENTIFIER, projectReplicaDatabaseName} from "../database/project-replica-contract"
import {projectReplicaTransport} from "../database/project-replica-transport"
import secrets from "./secrets"
import {getSessionTokenSync} from "../shared/session-option"
import {reportProjectSyncError} from "../shared/project-sync-reporting"
import {resolveOfflineGrantSigningKeys} from "../../../shared/offline-grant-signing-keys.js"
import {resolveWebsocketUrl} from "../shared/backend-api"
import {projectSyncConnectivity} from "../sync/project-sync-connectivity"

/**
 * Reads query params without assuming a browser-style `window.location`.
 * @returns {URLSearchParams} Query params exposed by the current runtime URL.
 */
function runtimeSearchParams() {
  if (typeof window === "undefined" || !window.location) return new URLSearchParams()

  return new URLSearchParams(window.location.search)
}

/**
 * Detects system-test launches from public env or the URL flag.
 * @param {URLSearchParams} searchParams Runtime URL query params.
 * @returns {boolean} Whether the app was launched by the system-test harness.
 */
function systemTestEnabled(searchParams) {
  return expoPublicEnvFlag("EXPO_PUBLIC_SYSTEM_TEST") || searchParams.get("systemTest") === "true"
}

/**
 * Enables real project synchronization only for focused replica system tests.
 * @param {URLSearchParams} searchParams Runtime URL query params.
 * @returns {boolean} Whether the system-test runtime may use sync transport.
 */
function systemTestProjectSyncEnabled(searchParams) {
  return expoPublicEnvFlag("EXPO_PUBLIC_SYSTEM_TEST_PROJECT_SYNC") || searchParams.get("systemTestProjectSync") === "true"
}

/**
 * Detects the explicit system-test query-log opt-in.
 * @param {URLSearchParams} searchParams Runtime URL query params.
 * @returns {boolean} Whether verbose frontend query logs were explicitly requested.
 */
function systemTestQueryLoggingEnabled(searchParams) {
  return expoPublicEnvFlag("EXPO_PUBLIC_SYSTEM_TEST_QUERY_LOGGING") || searchParams.get("systemTestQueryLogging") === "true"
}

/**
 * Resolves Velocious to `test` for browser system tests.
 * @param {URLSearchParams} searchParams Runtime URL query params.
 * @returns {string} Velocious runtime environment.
 */
function resolveEnvironment(searchParams) {
  if (systemTestEnabled(searchParams)) return "test"

  return expoPublicEnv("EXPO_PUBLIC_VELOCIOUS_ENV") || process.env.NODE_ENV || "development"
}

/**
 * Enables frontend SQL logs only when explicitly requested.
 * @param {URLSearchParams} searchParams Runtime URL query params.
 * @returns {{queryLogging: true} | undefined} Optional frontend logging override.
 */
function resolveLogging(searchParams) {
  if (!systemTestQueryLoggingEnabled(searchParams)) return undefined

  return {queryLogging: true}
}

/**
 * Resolves the keys the backend signs offline sync grants with, so the replica
 * can verify the grants it receives. Production reads them from the deployed
 * frontend secrets; until they are provisioned the list stays empty and offline
 * sync remains disabled instead of rejecting every grant it fetches.
 * @returns {OfflineGrantSigningKey[]} Offline-grant verification keys for the active environment.
 */
function resolveSyncSigningKeys() {
  const configured = /** @type {{offlineGrantSigningKeys?: OfflineGrantSigningKey[] | undefined}} */ (
    /** @type {Record<string, unknown>} */ (secrets)
  )?.offlineGrantSigningKeys

  return resolveOfflineGrantSigningKeys({configured, environment})
}

const searchParams = runtimeSearchParams()
const environment = resolveEnvironment(searchParams)
const logging = resolveLogging(searchParams)

const databaseMaster = /** @type {const} */ ({
  driver: SqliteDriver,

  /**
   * Resolves bundled sql.js wasm asset paths.
   * @param {string} file Requested sql.js asset filename.
   * @returns {string} Local asset path.
   */
  locateFile: (file) => `/${file}`,
  migrations: true,
  name: "awesome-tasks",
  poolType: SingleMultiUsePool,
  record: {
    transactions: false
  },
  reset: false,
  type: "sqlite"
})

const databaseForEnvironment = (name) => ({
  default: {...databaseMaster, name: `awesome-tasks-${name}`},
  [PROJECT_REPLICA_DATABASE_IDENTIFIER]: {
    ...databaseMaster,
    name: `awesome-tasks-${name}-unresolved-project-replica`,
    tenantOnly: true
  }
})

const configuration = new Configuration({
  database: {
    development: databaseForEnvironment("development"),
    production: databaseForEnvironment("production"),
    staging: databaseForEnvironment("staging"),
    test: databaseForEnvironment("test")
  },
  environment,
  environmentHandler: new BrowserEnvironmentHandler(),
  frontendTenantSqlite: {maxOpenHandles: 2},
  logging,
  initializeModels: async ({configuration: config}) => {
    // @ts-expect-error — Metro-specific require.context is not in Node's require type.
    const requireContextModels = require.context("../models", true, /^\.\/(.+)\.js$/)
    const velociousDatabaseInitializer = new VelociousDatabaseInitializer({requireContext: requireContextModels})

    await velociousDatabaseInitializer.initialize({configuration: config})
  },
  locale: () => "en",
  localeFallbacks: {
    da: ["da", "en", "de"],
    de: ["de", "en", "da"],
    en: ["en", "da", "de"]
  },
  locales: ["da", "de", "en"],
  sync: {
    offlineGrantSigningKeys: resolveSyncSigningKeys(),
    client: {
      authenticationToken: () => {
        const token = getSessionTokenSync()
        if (!token) throw new Error("Project sync requires an authenticated session")
        return token
      },
      isOnline: () => (!systemTestEnabled(searchParams) || systemTestProjectSyncEnabled(searchParams)) && Boolean(getSessionTokenSync()) &&
        projectSyncConnectivity.isOnline(),
      mountPath: "/velocious/sync",
      onError: reportProjectSyncError,
      transport: projectReplicaTransport,
      websocketUrl: () => {
        const params = typeof window === "undefined" || !window.location
          ? {}
          : Object.fromEntries(new URLSearchParams(window.location.search).entries())
        return resolveWebsocketUrl(params)
      }
    }
  },
  tenantDatabaseResolver: ({identifier, tenant}) => {
    if (identifier !== PROJECT_REPLICA_DATABASE_IDENTIFIER) return undefined
    return {name: projectReplicaDatabaseName({environment, projectId: tenant.projectId, userId: tenant.userId})}
  }
})

configuration.setCurrent()

export default configuration
